import { useState } from 'react';
import { PASSWORD_MIN_LENGTH } from '../../../shared/constants.ts';
import { passwordProblem } from '../../../shared/password-policy.ts';
import { randomPassword } from '../../../shared/password.ts';
import buttons from '../../styles/buttons.module.css';
import { FormActions, TextField } from '../form/fields.tsx';
import editor from './editor.module.css';

// One password form for four moments: changing one's own (the current one
// proves it is really them), replacing a handover password at first
// sign-in, an admin setting someone else's, and a reset link. A generated
// password is shown in clear so it can be passed on. The password policy
// (shared/password-policy.ts) runs here before anything is sent, with the
// account's username where the form knows it; the Worker runs it again.

export interface Passwords {
  readonly current: string;
  readonly next: string;
}

/** What the policy asks for, in one line under the field. */
const PASSWORD_HINT = `At least ${String(PASSWORD_MIN_LENGTH)} characters; not your username, not digits alone, not a common word`;

interface PasswordFormProps {
  readonly askCurrent: boolean;
  readonly submitLabel: string;
  readonly pending: boolean;
  readonly error?: string | undefined;
  /** Offer a generated password, for an account someone else will use. */
  readonly allowGenerate?: boolean | undefined;
  /** Whose password this is, so the policy can refuse one containing the name. */
  readonly username?: string | undefined;
  readonly onSubmit: (passwords: Passwords) => void;
  readonly onCancel?: (() => void) | undefined;
}

type Problems = Partial<Record<'current' | 'next' | 'confirm', string>>;

export function PasswordForm({
  askCurrent,
  submitLabel,
  pending,
  error,
  allowGenerate = false,
  username = '',
  onSubmit,
  onCancel,
}: PasswordFormProps) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [shown, setShown] = useState(false);
  const [problems, setProblems] = useState<Problems>({});

  const check = (): Problems => {
    const found: Problems = {};
    if (askCurrent && current === '') {
      found.current = 'Enter your current password';
    }
    const policy = passwordProblem(next, username);
    if (next.length < PASSWORD_MIN_LENGTH) {
      found.next = `At least ${String(PASSWORD_MIN_LENGTH)} characters`;
    } else if (policy !== null) {
      found.next = policy;
    }
    if (confirm !== next) {
      found.confirm = 'The passwords do not match';
    }
    return found;
  };

  return (
    <form
      aria-label={submitLabel}
      onSubmit={(event) => {
        event.preventDefault();
        const found = check();
        setProblems(found);
        if (Object.keys(found).length === 0) {
          onSubmit({ current, next });
        }
      }}
    >
      {askCurrent ? (
        <TextField
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={setCurrent}
          error={problems.current}
        />
      ) : null}
      {allowGenerate ? (
        <div className={editor['header']}>
          <button
            className={`${buttons['secondary'] ?? ''} ${buttons['small'] ?? ''}`}
            type="button"
            onClick={() => {
              const generated = randomPassword();
              setNext(generated);
              setConfirm(generated);
              setShown(true);
            }}
          >
            Generate a password
          </button>
          <p className={editor['empty']}>A generated one stays visible so you can pass it on.</p>
        </div>
      ) : null}
      <TextField
        label="New password"
        type={shown ? 'text' : 'password'}
        autoComplete="new-password"
        value={next}
        onChange={setNext}
        error={problems.next}
        hint={PASSWORD_HINT}
      />
      <TextField
        label="Confirm new password"
        type={shown ? 'text' : 'password'}
        autoComplete="new-password"
        value={confirm}
        onChange={setConfirm}
        error={problems.confirm}
      />
      <FormActions submitLabel={submitLabel} pending={pending} onCancel={onCancel} error={error} />
    </form>
  );
}
